import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { dpiaAPI, aiAssessmentAPI, liaAPI, tiaAPI, prescreenAdminAPI, tasksAPI, entitiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { Plus, Shield, Bot, Scale, Globe, ChevronDown, Inbox } from 'lucide-react';
import { toast } from 'sonner';

import { DPIATable } from './components/DPIATable';
import { AIAssessmentTable } from './components/AIAssessmentTable';
import { LIATable } from './components/LIATable';
import { TIATable } from './components/TIATable';
import { PreScreenTable } from './components/PreScreenTable';
import { PendingPreScreenTable } from './components/PendingPreScreenTable';
import { CreateTempGuestDialog } from '../../components/guest/CreateTempGuestDialog';
import { OverviewCards } from './components/OverviewCards';
import { ModuleTabs } from './components/ModuleTabs';


export default function RiskAssessmentsPage() {
  const navigate = useNavigate();
  const { isDPO, isComplianceReadOnly: isReadOnly, isClientAdmin, isEntityAdmin, user } = useAuth();
  const { selectedClientId, selectedEntityId, selectedDepartmentId } = useGlobalContext();
  const [activeTab, setActiveTab] = useState('prescreen');
  const [dpiaAssessments, setDpiaAssessments] = useState([]);
  const [aiAssessments, setAiAssessments] = useState([]);
  const [liaAssessments, setLiaAssessments] = useState([]);
  const [tiaAssessments, setTiaAssessments] = useState([]);
  const [prescreenSubmissions, setPrescreenSubmissions] = useState([]);
  const [pendingTokens, setPendingTokens] = useState([]);
  const [sendPreScreenOpen, setSendPreScreenOpen] = useState(false);
  const [dpiaTaskCounts, setDpiaTaskCounts] = useState({});
  const [entities, setEntities] = useState([]);

  useEffect(() => {
    if (isDPO || isClientAdmin) {
      entitiesAPI.getAll(isClientAdmin ? user?.client_id : undefined)
        .then(res => setEntities(res.data || []))
        .catch(() => {});
    } else if (isEntityAdmin && user?.entity_id) {
      setEntities([{ id: user.entity_id, entity_name: user.entity_name || 'Your Entity' }]);
    }
  }, [isDPO, isClientAdmin, isEntityAdmin, user]);

  const fetchData = useCallback(async () => {
    try {
      const [dpiaRes, aiRes, liaRes, tiaRes, prescreenRes, tokensRes, dpiaCountsRes] = await Promise.all([
        dpiaAPI.getAll(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined),
        aiAssessmentAPI.getAll(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined),
        liaAPI.getAll(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined).catch(() => ({ data: [] })),
        tiaAPI.getAll(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined).catch(() => ({ data: [] })),
        isDPO ? prescreenAdminAPI.getAll({ limit: 100 }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        isDPO ? prescreenAdminAPI.listTokens({ status: 'pending', limit: 100 }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        tasksAPI.getCountsBatch('dpia').catch(() => ({ data: {} })),
      ]);
      setDpiaAssessments(dpiaRes.data);
      setAiAssessments(aiRes.data);
      setLiaAssessments(liaRes.data);
      setTiaAssessments(tiaRes.data);
      setPrescreenSubmissions(prescreenRes.data || []);
      setPendingTokens(tokensRes.data || []);
      setDpiaTaskCounts(dpiaCountsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      toast.error('Failed to fetch assessments');
    }
  }, [selectedEntityId, selectedClientId, selectedDepartmentId, isDPO]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="risk-assessments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Risk Assessments</h1>
            <p className="text-muted-foreground">DPIA, LIA, and TIA assessments</p>
          </div>
          {!isReadOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="new-assessment-dropdown-btn" className="bg-teal-500 hover:bg-teal-600">
                  <Plus className="h-4 w-4 mr-2" />New Assessment<ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem onClick={() => navigate('/risk/dpia/new')} className="cursor-pointer py-3" data-testid="menu-new-dpia">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-950 flex items-center justify-center flex-shrink-0"><Shield className="h-4 w-4 text-teal-600 dark:text-teal-400" /></div>
                    <div><p className="font-medium text-sm">Data Protection Impact Assessment</p><p className="text-xs text-muted-foreground">Full DPIA workflow</p></div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/risk/dpia-intake')} className="cursor-pointer py-3" data-testid="menu-dpia-intake">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-950 flex items-center justify-center flex-shrink-0"><Inbox className="h-4 w-4 text-blue-600 dark:text-blue-400" /></div>
                    <div><p className="font-medium text-sm">DPIA Intake</p><p className="text-xs text-muted-foreground">Send questionnaire to clients</p></div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/risk/ai/new')} className="cursor-pointer py-3" data-testid="menu-new-ai">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-950 flex items-center justify-center flex-shrink-0"><Bot className="h-4 w-4 text-purple-600 dark:text-purple-400" /></div>
                    <div><p className="font-medium text-sm">AI System Assessment</p><p className="text-xs text-muted-foreground">NIST AI RMF framework</p></div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/risk/lia/new')} className="cursor-pointer py-3" data-testid="menu-new-lia">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-950 flex items-center justify-center flex-shrink-0"><Scale className="h-4 w-4 text-amber-600 dark:text-amber-400" /></div>
                    <div><p className="font-medium text-sm">Legitimate Interest Assessment</p><p className="text-xs text-muted-foreground">LIA balancing test</p></div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/risk/tia/new')} className="cursor-pointer py-3" data-testid="menu-new-tia">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-950 flex items-center justify-center flex-shrink-0"><Globe className="h-4 w-4 text-green-600 dark:text-green-400" /></div>
                    <div><p className="font-medium text-sm">Transfer Impact Assessment</p><p className="text-xs text-muted-foreground">International data transfers</p></div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>


        {/* Pending Invitations Panel — spec §Component order row 2: must sit
            above the Overview cards, per the Part 2 screenshot reference. */}
        {isDPO && (
          <PendingPreScreenTable
            tokens={pendingTokens}
            onRefresh={fetchData}
            onSendNewLink={() => setSendPreScreenOpen(true)}
          />
        )}

        {/* Overview Cards — spec §Component order row 3. */}
        <OverviewCards
          activeTab={activeTab}
          onTabChange={setActiveTab}
          prescreenCount={prescreenSubmissions.length}
          dpiaCount={dpiaAssessments.length}
          liaCount={liaAssessments.length}
          tiaCount={tiaAssessments.length}
        />

        {/* Module Tabs (NEW) */}
        <ModuleTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Dynamic Table (NEW) - switches per activeTab */}
        <div className="mt-6">
          {/* Pre-screen Tab */}
          {activeTab === 'prescreen' && (
            isDPO ? (
              prescreenSubmissions.length > 0 ? (
                <PreScreenTable submissions={prescreenSubmissions} onRefresh={fetchData} navigate={navigate} />
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <p className="text-muted-foreground mb-4">No pre-screen submissions yet.</p>
                  {!isReadOnly && (
                    <Button onClick={() => setSendPreScreenOpen(true)} variant="outline">
                      + New Pre-Screen Assessment
                    </Button>
                  )}
                </div>
              )
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <p className="text-muted-foreground">Pre-screen assessments are only visible to DPOs.</p>
              </div>
            )
          )}

          {/* DPIA Tab */}
          {activeTab === 'dpia' && (
            dpiaAssessments.length > 0 ? (
              <DPIATable assessments={dpiaAssessments} onRefresh={fetchData} taskCounts={dpiaTaskCounts} />
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <p className="text-muted-foreground mb-4">No DPIA assessments yet.</p>
                {!isReadOnly && (
                  <Button onClick={() => navigate('/risk/dpia/new')} variant="outline">
                    + New DPIA
                  </Button>
                )}
              </div>
            )
          )}

          {/* LIA Tab */}
          {activeTab === 'lia' && (
            liaAssessments.length > 0 ? (
              <LIATable assessments={liaAssessments} onRefresh={fetchData} />
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <p className="text-muted-foreground mb-4">No LIA assessments yet.</p>
                {!isReadOnly && (
                  <Button onClick={() => navigate('/risk/lia/new')} variant="outline">
                    + New LIA
                  </Button>
                )}
              </div>
            )
          )}

          {/* TIA Tab */}
          {activeTab === 'tia' && (
            tiaAssessments.length > 0 ? (
              <TIATable assessments={tiaAssessments} onRefresh={fetchData} />
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <p className="text-muted-foreground mb-4">No TIA assessments yet.</p>
                {!isReadOnly && (
                  <Button onClick={() => navigate('/risk/tia/new')} variant="outline">
                    + New TIA
                  </Button>
                )}
              </div>
            )
          )}

          {/* AI Assessment Tab (disabled, coming soon) */}
          {activeTab === 'ai' && (
            <div className="text-center py-12 bg-muted/30 rounded-lg">
              <p className="text-muted-foreground mb-4">AI Assessment module is under development.</p>
            </div>
          )}
        </div>

        <CreateTempGuestDialog
          open={sendPreScreenOpen}
          onOpenChange={setSendPreScreenOpen}
          defaultModule="prescreen"
          entities={entities}
          lockedEntityId={isEntityAdmin ? user?.entity_id : null}
          onCreated={fetchData}
        />
      </div>
    </DashboardLayout>
  );
}
